import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtCLP } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Info, HandCoins, CalendarDays, Shield, AlertTriangle as AlertTriangleIcon, CheckCircle2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const ACTIONS = {
  info: {
    label: "Solicitar información",
    icon: <Info className="w-5 h-5" />,
    desc: "Pregunta al equipo DerechoLlave por más detalles del negocio.",
    color: "bg-slate-900 hover:bg-slate-800 text-white",
  },
  offer: {
    label: "Hacer una oferta",
    icon: <HandCoins className="w-5 h-5" />,
    desc: "Propón un monto. El equipo lo gestiona con el vendedor.",
    color: "btn-investment",
  },
  visit: {
    label: "Solicitar visita",
    icon: <CalendarDays className="w-5 h-5" />,
    desc: "Coordinamos una visita acompañada al local.",
    color: "bg-amber-500 hover:bg-amber-600 text-slate-900",
  },
};

function MoneyInput({ value, onChange, ...props }) {
  const display = value === "" || value == null ? "" : "$" + Number(String(value).replace(/\D/g, "") || "0").toLocaleString("es-CL");
  return (
    <Input {...props} type="text" inputMode="numeric" value={display}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} />
  );
}

export default function InvestorActions({ business, salePrice }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kind, setKind] = useState(null);
  const [step, setStep] = useState(1); // 1 = intermediation, 2 = visit protocol (only for visits), 3 = action form
  const [accepted, setAccepted] = useState(false);
  const [visitProtocol, setVisitProtocol] = useState(false);
  const [form, setForm] = useState({ message: "", offer_amount: "", visit_date: "", visit_alt_date: "" });
  const [submitting, setSubmitting] = useState(false);

  const open = (k) => {
    if (!user) { toast.error("Inicia sesión como inversionista"); navigate("/login"); return; }
    if (user.role !== "investor") { toast.error("Sólo inversionistas pueden realizar esta acción"); return; }
    setKind(k);
    setStep(1);
    setAccepted(false);
    setVisitProtocol(false);
    setForm({
      message: k === "info" ? "Hola, me interesa esta oportunidad. Quisiera más información." : "",
      offer_amount: k === "offer" ? String(Math.round(salePrice * 0.9)) : "",
      visit_date: "", visit_alt_date: "",
    });
  };

  const close = () => { setKind(null); setStep(1); setAccepted(false); setVisitProtocol(false); };

  const submit = async () => {
    if (!accepted) { toast.error("Debes aceptar el acuerdo de intermediación"); return; }
    if (kind === "visit" && !visitProtocol) { toast.error("Debes aceptar el Protocolo de Visita"); return; }
    setSubmitting(true);
    try {
      const payload = {
        business_id: business.id,
        kind,
        intermediation_accepted: true,
        message: form.message || null,
      };
      if (kind === "offer") {
        const amt = parseFloat(form.offer_amount);
        if (!amt || amt <= 0) { toast.error("Ingresa el monto de tu oferta"); setSubmitting(false); return; }
        payload.offer_amount = amt;
      }
      if (kind === "visit") {
        if (!form.visit_date) { toast.error("Selecciona una fecha de visita"); setSubmitting(false); return; }
        payload.visit_date = new Date(form.visit_date).toISOString();
        if (form.visit_alt_date) payload.visit_alt_date = new Date(form.visit_alt_date).toISOString();
        payload.visit_protocol_accepted = true;
      }
      await api.post("/requests", payload);
      const map = { info: "Solicitud enviada", offer: "Oferta enviada", visit: "Visita solicitada" };
      toast.success(map[kind] + ". El equipo te contactará pronto.");
      close();
      navigate("/investor/requests");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  const cfg = kind ? ACTIONS[kind] : null;
  const suggestedMin = Math.round(salePrice * 0.85);

  return (
    <>
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-2.5">
        <p className="overline text-emerald-700 mb-3">¿Te interesa este negocio?</p>
        {Object.entries(ACTIONS).map(([k, c]) => (
          <button
            key={k}
            onClick={() => open(k)}
            data-testid={`action-${k}`}
            className={`w-full ${c.color} rounded-lg px-4 py-3 flex items-center gap-3 transition`}
          >
            <span className="w-9 h-9 rounded-md bg-white/15 flex items-center justify-center shrink-0">{c.icon}</span>
            <span className="text-left">
              <span className="block font-semibold">{c.label}</span>
              <span className="block text-xs opacity-80 font-normal">{c.desc}</span>
            </span>
          </button>
        ))}
      </div>

      <Dialog open={!!kind} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-lg">
          {step === 1 && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <DialogTitle>Acuerdo de intermediación</DialogTitle>
                    <DialogDescription className="text-xs">Paso 1{kind === "visit" ? " de 3" : " de 2"} · Aceptación obligatoria</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-3 mt-2 text-sm text-slate-700 leading-relaxed">
                <p>Para avanzar con esta gestión, debes reconocer y aceptar las siguientes condiciones de intermediación de <strong>DerechoLlave</strong>:</p>
                <ul className="space-y-2 list-none pl-0">
                  <Bullet>Tu interlocutor durante el proceso es <strong>el equipo DerechoLlave</strong>. El vendedor no participa del chat ni recibe tu identidad durante las etapas iniciales.</Bullet>
                  <Bullet>Está prohibido intentar ubicar o contactar al vendedor por fuera (teléfono, email, redes, presencial). El contacto directo sólo se habilita en la visita acompañada o en el cierre, y siempre con autorización del equipo.</Bullet>
                  <Bullet>Si la operación se concreta a partir del contacto generado en DerechoLlave —independiente del canal donde se firme— la plataforma mantiene su rol de gestor y su <strong>comisión acordada con el vendedor</strong> (entre 5% y 8%).</Bullet>
                  <Bullet>Si quiero retirarme del proceso, debo comunicarlo formalmente al equipo de la plataforma.</Bullet>
                  <Bullet>DerechoLlave es intermediario; la información del negocio es declarada por el vendedor y la decisión de compra es bajo mi responsabilidad. <Link to="/terminos" target="_blank" className="underline">Ver términos completos</Link>.</Bullet>
                </ul>
                <label className="flex items-start gap-3 p-3 mt-4 border border-zinc-200 rounded-lg bg-zinc-50 cursor-pointer">
                  <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="accept-intermediation" className="mt-0.5" />
                  <span className="text-xs text-slate-700">
                    Acepto el acuerdo de intermediación y reconozco que <strong>la plataforma DerechoLlave es el gestor del proceso</strong>. No intentaré contactar al vendedor por fuera ni eludir la comisión de la plataforma.
                  </span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close}>Cancelar</Button>
                <Button onClick={() => setStep(kind === "visit" ? 2 : 3)} disabled={!accepted} className="btn-primary" data-testid="continue-to-action">
                  Continuar
                </Button>
              </DialogFooter>
            </>
          )}

          {step === 2 && kind === "visit" && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                    <AlertTriangleIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <DialogTitle>Protocolo de Visita</DialogTitle>
                    <DialogDescription className="text-xs">Paso 2 de 3 · Aceptación obligatoria con cláusula de no-circunvención</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-3 mt-2 text-sm text-slate-700 leading-relaxed max-h-[380px] overflow-y-auto pr-1">
                <p>Las visitas físicas son el punto más sensible del proceso. Por eso, antes de coordinarla, debes aceptar las siguientes condiciones específicas:</p>

                <div className="border-l-2 border-amber-500 pl-4 space-y-2">
                  <p className="font-semibold text-slate-900">1. Acompañamiento obligatorio</p>
                  <p>Toda visita es <strong>acompañada por un representante de DerechoLlave</strong>, presencial o por videollamada en vivo. No se autoriza ninguna visita sin la presencia de la plataforma.</p>
                </div>

                <div className="border-l-2 border-amber-500 pl-4 space-y-2">
                  <p className="font-semibold text-slate-900">2. Dirección confidencial</p>
                  <p>La dirección exacta del negocio se entrega por canal seguro de la plataforma <strong>hasta 24 horas antes</strong> de la visita confirmada. No la difundiré ni se la entregaré a terceros.</p>
                </div>

                <div className="border-l-2 border-rose-500 pl-4 space-y-2 bg-rose-50/40 p-3 rounded">
                  <p className="font-semibold text-rose-900">3. Cláusula de no-circunvención</p>
                  <p>Si después de tomar contacto a través de DerechoLlave —incluida esta visita— concreto la compra del negocio (directamente o mediante terceros, sociedades vinculadas o familiares) <strong>en los siguientes 18 meses fuera de la plataforma</strong>, acepto pagar a DerechoLlave una indemnización equivalente a la <strong>comisión acordada con el vendedor (5%–8% del precio final)</strong>, más IVA y costos legales de cobranza.</p>
                  <p>Esta cláusula constituye un acuerdo serio entre las partes y es ejecutable según los términos publicados en <Link to="/terminos" target="_blank" className="underline">/terminos</Link>.</p>
                </div>

                <div className="border-l-2 border-slate-400 pl-4 space-y-2">
                  <p className="font-semibold text-slate-900">4. Trazabilidad</p>
                  <p>Se registrará fecha, hora, IP y hash digital de mi aceptación. La plataforma tomará constancia (firma o foto) el día de la visita, con valor de respaldo en caso de controversia.</p>
                </div>

                <label className="flex items-start gap-3 p-3 mt-4 border-2 border-rose-300 rounded-lg bg-rose-50 cursor-pointer">
                  <Checkbox checked={visitProtocol} onCheckedChange={setVisitProtocol} data-testid="accept-visit-protocol" className="mt-0.5" />
                  <span className="text-xs text-slate-800">
                    He leído y acepto el <strong>Protocolo de Visita</strong> íntegramente, incluyendo el <strong>acompañamiento obligatorio</strong>, la <strong>confidencialidad de la dirección</strong> y la <strong>cláusula de no-circunvención a 18 meses</strong> con su indemnización correspondiente.
                  </span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(1)}>Volver</Button>
                <Button onClick={() => setStep(3)} disabled={!visitProtocol} className="btn-primary" data-testid="continue-after-protocol">
                  Continuar
                </Button>
              </DialogFooter>
            </>
          )}

          {step === 3 && cfg && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    {cfg.icon}
                  </div>
                  <div>
                    <DialogTitle>{cfg.label}</DialogTitle>
                    <DialogDescription className="text-xs">Paso {kind === "visit" ? "3 de 3" : "2 de 2"} · Completa los datos</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {kind === "offer" && (
                  <div>
                    <Label>Monto de tu oferta</Label>
                    <MoneyInput value={form.offer_amount} onChange={(v) => setForm({ ...form, offer_amount: v })} data-testid="offer-amount-input" />
                    <p className="text-xs text-slate-500 mt-1.5">
                      Precio de venta publicado: <strong>{fmtCLP(salePrice)}</strong>. Las ofertas bajo {fmtCLP(suggestedMin)} suelen ser rechazadas.
                    </p>
                  </div>
                )}

                {kind === "visit" && (
                  <>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label>Fecha preferida</Label>
                        <Input type="datetime-local" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} data-testid="visit-date-input" />
                      </div>
                      <div>
                        <Label>Fecha alternativa (opcional)</Label>
                        <Input type="datetime-local" value={form.visit_alt_date} onChange={(e) => setForm({ ...form, visit_alt_date: e.target.value })} />
                      </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                      📍 La dirección exacta se enviará por la plataforma hasta 24h antes. La visita será acompañada por un representante de DerechoLlave.
                    </div>
                  </>
                )}

                <div>
                  <Label>{kind === "info" ? "Mensaje" : "Mensaje adicional (opcional)"}</Label>
                  <Textarea
                    data-testid="action-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={kind === "info" ? 5 : 3}
                    placeholder={
                      kind === "offer" ? "Comenta condiciones, financiamiento, plazos, etc." :
                      kind === "visit" ? "Comenta cuántas personas asistirán, qué quisieras revisar, etc." :
                      "Cuéntale al equipo qué te interesa saber del negocio."
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1.5">
                    Tu mensaje va al equipo DerechoLlave, no al vendedor.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(kind === "visit" ? 2 : 1)}>Volver</Button>
                <Button onClick={submit} disabled={submitting} className="btn-investment" data-testid="submit-action-btn">
                  {submitting ? "Enviando..." : "Enviar"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
